// The running cat, independent of where it is drawn: plays the steps the brain plans, keeps the cat on its platform as windows
// move (it rides along a dragged window; if its window goes away it falls), lets the user pick it up and drop it. Runs in the
// app's main process (which knows the windows and the cursor at once) and in the headless simulation; the overlays only draw it.
import { Brain, FnStep } from './brain.js';
import { AirStep, FallStep, HeldStep, HoldStep, LeapStep, groundY } from './steps.js';
import { buildWorld, floorAt, platformAt, samePlatform } from './world.js';

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const STAY = new Set(['sit', 'stand', 'lie', 'curl']); // poses it can stay in, looping in place

export class Controller {
  constructor(rig, H, raw) {
    this.rig = rig;
    this.H = H;
    this.world = buildWorld(raw, H);
    this.brain = new Brain(rig);
    const floor = this.world.floorPlatform;
    this.cat = {
      x: (floor.x0 + floor.x1) / 2,
      y: floor.y,
      dir: 1,
      pose: 'stand',
      surface: floor,
      wall: null,
      clip: null,
      frame: 0,
      throwVelocity: null,
      show(clip, k) {
        this.clip = clip;
        this.frame = k;
      },
    };
    this.pointer = { x: 0, y: 0, vx: 0, vy: 0, t: 0 };
    this.step = null;
    this.queue = [];
    this.held = null;
    this.clamped = {}; // ticks the cat had to be put back onto its platform, by step
    this.followMoves = true; // false: no move hook, so a dragged window throws the cat off instead of carrying it
    const self = this;
    this.ctx = {
      get world() {
        return self.world;
      },
      H,
      pointer: this.pointer,
      queueFront: (s) => self.queue.unshift(s),
    };
  }

  begin(s) {
    this.step = s;
    s.start(this.cat, this.ctx);
  }

  nextStep() {
    // Staying put (its chat is open): only what it does in place
    if (!this.queue.length && this.staying && STAY.has(this.cat.pose)) this.queue = this.brain.loop(this.cat.pose);
    if (!this.queue.length) this.queue = this.brain.next(this.cat, this.world) ?? [];
    if (!this.queue.length) this.queue = this.brain.loop(this.cat.pose);
    this.begin(this.queue.shift());
  }

  /** The ground went away under the cat: drop from where it is. */
  startle() {
    this.queue = [];
    this.begin(this.brain.fall());
  }

  /** In flight (not the landing frames after it: those are on the ground and ride along with a moving window). */
  airborne() {
    return (this.step instanceof AirStep || this.step instanceof FallStep) && this.step.phase !== 'post';
  }

  /** A new picture of the displays and windows (from the poll). */
  setWorld(raw) {
    const old = this.world;
    this.world = buildWorld(raw, this.H);
    const cat = this.cat;
    if (this.held) return;
    if (cat.wall) {
      const w = this.world.walls.find((x) => x.key === cat.wall.key);
      if (!w || Math.abs(w.x - cat.wall.x) > 2 || cat.y < w.y0 - this.H) this.startle();
      else cat.wall = w;
    } else if (cat.surface) {
      const p = samePlatform(this.world, cat.surface);
      // Standing on a window that moved: move along with it (normally already done by the move hook)
      const key = cat.surface.keys.length === 1 && !cat.surface.keys[0].startsWith('floor:') ? cat.surface.keys[0] : null;
      let shift = 0;
      if (key) {
        const id = key.split(':')[0];
        const a = old.rects.get(id);
        const b = this.world.rects.get(id);
        if (a && b) shift = b.x - a.x;
      }
      if (!this.followMoves && shift) this.startle();
      else if (p && cat.x + shift >= p.x0 - 2 && cat.x + shift <= p.x1 + 2) {
        if (Math.abs(p.y - cat.surface.y) > 1 || shift || p.x0 !== cat.surface.x0 || p.x1 !== cat.surface.x1) this.queue = []; // re-plan once this step ends
        cat.x += shift;
        cat.y = p.y;
        cat.surface = p;
      } else this.startle();
    }
  }

  /** Windows moving right now (from the move hook, ahead of the next poll): what is built on their edges moves with them, the cat too. */
  applyMoves(moves) {
    const world = this.world;
    const cat = this.cat;
    const onDisplay = (r) => world.displays.some((d) => r.x + r.w > d.x && r.x < d.x + d.w && r.y + r.h > d.y && r.y < d.y + d.h);
    for (const { id, rect } of moves) {
      const old = world.rects.get(id);
      if (!old) continue;
      // Minimized, hidden or moved off every display: the window is gone (what stood on it falls); the next poll rebuilds the rest
      if (!rect || !onDisplay(rect)) {
        world.rects.delete(id);
        // (a platform joined with other windows' edges is left to the next poll, which knows what remains of it)
        world.platforms = world.platforms.filter((p) => !(p.keys.length === 1 && p.keys[0].startsWith(id + ':')));
        world.walls = world.walls.filter((w) => w.win !== id);
        const onIt = (cat.surface?.keys.length === 1 && cat.surface.keys[0].startsWith(id + ':')) || cat.wall?.win === id;
        if (onIt && !this.held) this.startle();
        continue;
      }
      world.rects.set(id, rect);
      const dx = rect.x - old.x;
      const dy = rect.y - old.y;
      if (!dx && !dy && rect.w === old.w && rect.h === old.h) continue;
      for (const p of world.platforms) {
        if (p.keys.length !== 1) continue; // merged with other edges: left to the next poll
        const [wid, edge] = p.keys[0].split(':');
        if (wid !== id) continue;
        p.x0 += dx;
        p.x1 += dx;
        p.y = edge === 'top' ? rect.y : rect.y + rect.h;
      }
      for (const w of world.walls) {
        if (w.win !== id) continue;
        w.x = w.side === 'left' ? rect.x : rect.x + rect.w;
        w.y0 += dy;
        w.y1 += dy;
        w.top = rect.y;
      }
      if (this.held) continue;
      const onIt = cat.surface && cat.surface.keys.length === 1 && cat.surface.keys[0].startsWith(`${id}:`);
      if (onIt && !this.airborne()) {
        cat.x += dx;
        cat.y = groundY(cat);
      } else if (cat.wall?.win === id) {
        cat.x += dx;
        cat.y += dy;
      }
    }
  }

  // ---------------- the user picks the cat up by the scruff ----------------

  movePointer(x, y, t) {
    const p = this.pointer;
    const dt = Math.max(0.001, (t - p.t) / 1000);
    if (p.t) {
      p.vx = p.vx * 0.6 + ((x - p.x) / dt) * 0.4;
      p.vy = p.vy * 0.6 + ((y - p.y) / dt) * 0.4;
    }
    p.x = x;
    p.y = y;
    p.t = t;
  }

  grab(x, y, t) {
    if (this.held) return;
    this.movePointer(x, y, t);
    this.pointer.vx = this.pointer.vy = 0;
    this.queue = [];
    this.held = new HeldStep(this.rig.get('hang__struggle__hang'));
    this.brain.intent = '被拎起来了';
    this.begin(this.held);
  }

  release() {
    if (!this.held) return;
    const H = this.H;
    this.cat.throwVelocity = [clamp(this.pointer.vx, -6 * H, 6 * H), clamp(this.pointer.vy, -4 * H, 4 * H)];
    this.held.released = true;
    this.held = null;
  }

  /** Its first appearance: it waits up in the middle of the primary display (unseen while the overlays load) until dropIn. */
  hover() {
    const d = this.world.displays[0];
    const loop = this.rig.get('fall__fall__fall') ?? this.rig.from('sit')[0];
    Object.assign(this.cat, { x: d.x + d.w / 2, y: d.y + d.h / 2, surface: null, wall: null, pose: 'fall' });
    this.queue = [];
    this.begin(new HoldStep(loop, Infinity, 'jump'));
  }

  /** Falls from where it hovers, lands sitting, calls onLanded and stays sitting a while before carrying on as usual. */
  dropIn(sitSeconds, onLanded) {
    if (this.held) return onLanded();
    this.begin(this.brain.fall({ floorOnly: true }));
    this.brain.lastLoop = sitSeconds;
    this.queue = [new FnStep(onLanded), new HoldStep(this.rig.from('sit')[0], sitSeconds)];
  }

  /**
   * Leap to a point however far (into the app's window), shrinking to `scale` on the way; onLanded once it stands there. It then
   * holds still: the window carries on with it.
   */
  leapTo(x, y, scale, onLanded) {
    if (this.held) return false;
    this.queue = [];
    const clip = this.rig.get('crouch__jump__stand_stare');
    if (!clip) return false;
    this.begin(new LeapStep(clip, { x, y }, { scale, onLanded }));
    return true;
  }

  /** Back from the app's window: it is at (x, y) (at the window's size, growing back as it falls) and falls from there. */
  dropAt(x, y, dir, scale = 1) {
    Object.assign(this.cat, { x, y, dir, scale, surface: null, wall: null });
    this.held = null;
    this.queue = [];
    this.begin(this.brain.fall());
  }

  /** Arrived (in the app's window, from the desktop): standing at (x, y) in this pose; it goes on from there. */
  placeAt(x, y, pose, dir) {
    const cat = this.cat;
    Object.assign(cat, { x, y, dir, pose, wall: null });
    cat.surface = platformAt(this.world, x, y, 8);
    this.queue = [];
    if (!cat.surface) return this.startle();
    cat.y = cat.surface.y;
    this.step = null; // the brain picks what comes next on the next tick
  }

  /** Back on the primary display's floor. */
  reset() {
    const cat = this.cat;
    this.queue = [];
    this.held = null;
    cat.surface = this.world.floorPlatform;
    cat.wall = null;
    cat.x = (cat.surface.x0 + cat.surface.x1) / 2;
    cat.y = cat.surface.y;
    cat.pose = 'stand';
    this.begin(this.brain.loop('stand')[0]);
  }

  // ---------------- time ----------------

  tick(dt) {
    const cat = this.cat;
    const H = this.H;
    if (!this.step) this.nextStep();
    let left = dt;
    for (let guard = 0; guard < 8 && this.step.update(cat, left, this.ctx); guard++) {
      left = 0; // instant steps (turning) chain on into the next one within this tick
      this.nextStep();
    }
    // Walked past the end of its platform (it shrank or moved under the cat): it falls off
    const sf = cat.surface;
    if (sf && !this.held && !this.airborne() && (cat.x < sf.x0 - 0.15 * H || cat.x > sf.x1 + 0.15 * H)) this.startle();
    if (cat.surface && !cat.wall && !this.held && !this.airborne()) {
      // Just past an end (a landing or a shrinking window left it there): back onto the platform, so every plan has room
      const x = clamp(cat.x, cat.surface.x0, cat.surface.x1);
      if (Math.abs(x - cat.x) > 0.5) {
        const name = this.step?.clip?.name ?? this.step?.constructor.name;
        this.clamped[name] = (this.clamped[name] ?? 0) + 1; // (a plan that overshot its platform: should not happen)
      }
      cat.x = x;
      cat.y = groundY(cat);
    }
    // Back from the app's window at its size: grow back to full size
    if ((cat.scale ?? 1) < 1 && !(this.step instanceof LeapStep)) cat.scale = Math.min(1, cat.scale + dt * 1.2);
    this.brain.tick(this.step.tag, dt);
    if (!this.held) cat.x = clamp(cat.x, this.world.minX, this.world.maxX);
    // Fell below every floor (dragged off the bottom, a display went away): back onto the floor under it
    const floor = floorAt(this.world, cat.x, cat.y);
    if (!this.held && !this.airborne() && !cat.wall && cat.y > floor.y + H) {
      cat.surface = floor;
      cat.y = floor.y;
    }
  }

  /**
   * Clips the overlays should have loaded before they are needed: what is queued, everything that can start from the pose the
   * current step ends in (the brain may pick any of them next), and what can happen at any moment (falling, being picked up).
   */
  upcoming() {
    const names = new Set(['fall__fall__fall', 'fall__fall__sit', 'hang__struggle__hang', 'hang__drop_land__stand', 'hang__drop__sit']);
    for (const s of this.queue) if (s.clip) names.add(s.clip.name);
    const pose = this.step?.clip?.to ?? this.cat.pose;
    for (const c of this.rig.from(pose)) names.add(c.name);
    return [...names].sort();
  }

  /** What the overlays draw. */
  view() {
    const c = this.cat;
    return { clip: c.clip?.name ?? null, frame: c.frame, x: c.x, y: c.y, dir: c.dir, scale: c.scale ?? 1 };
  }

  debug() {
    const n = this.brain.needs;
    const c = this.cat;
    return [
      `姿势 ${c.pose}  朝向 ${c.dir > 0 ? '→' : '←'}  意图 ${this.brain.intent}`,
      `动作 ${this.step?.constructor.name} ${this.step?.clip?.name ?? ''}  队列 ${this.queue.length}`,
      `精力 ${n.energy.toFixed(0)}  无聊 ${n.boredom.toFixed(0)}  烦躁 ${n.annoyance.toFixed(0)}`,
    ];
  }
}
